import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { api } from '../api';
import SmartPro from './SmartPro';

const EnhancedDocumentsTab = () => {
  const { user } = useAuth();
  const safeUser = user || { role: 'homeowner' };
  
  // Note: currentSubTab is already defined below for documents/smart-pro
  
  // State management
  const [documents, setDocuments] = useState([]);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('governing');
  const [selectedVisibility, setSelectedVisibility] = useState('all');
  const [specificUsers, setSpecificUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterVisibility, setFilterVisibility] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterUploadedBy, setFilterUploadedBy] = useState('');
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [isEditing, setIsEditing] = useState(null);
  const [editData, setEditData] = useState({});
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  
  // Document review period setting
  const [documentReviewDays, setDocumentReviewDays] = useState(3);
  const [loadingReviewSetting, setLoadingReviewSetting] = useState(false);
  const [savingReviewSetting, setSavingReviewSetting] = useState(false);
  
  // HOA Files Pro Folders state
  const [availableFolders, setAvailableFolders] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);
  
  // Document permission settings - loaded from localStorage/settings
  const [documentSettings, setDocumentSettings] = useState({
    allowHomeownerDelete: false, // Off by default
    allowHomeownerUpload: false, // Off by default - must be explicitly enabled
    allowSecurityUpload: false, // Hidden by default
    allowMaintenanceUpload: false, // Hidden by default
    allowedUploadUsers: [] // Specific users who can upload documents
  });
  
  // Storage usage tracking
  const [storageUsage, setStorageUsage] = useState(null);
  const [loadingStorage, setLoadingStorage] = useState(false);
  
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Document categories
  const documentCategories = [
    { id: 'governing', name: 'Governing Documents', description: 'CC&Rs, Bylaws, Articles of Incorporation' },
    { id: 'financial', name: 'Financial Documents', description: 'Budgets, Financial Statements, Reserve Studies' },
    { id: 'owner_statements', name: 'Owner Statements', description: 'Property Ledger Statements, Account Summaries' },
    { id: 'policies', name: 'Policies & Rules', description: 'Rules & Regulations, Architectural Guidelines' },
    { id: 'bids_contracts', name: 'Bids and Contracts', description: 'Vendor Bids, Service Contracts, Agreement Documents' },
    { id: 'agendas_notices', name: 'Agendas and Notices', description: 'Meeting Agendas, Official Notices, Community Announcements' },
    { id: 'legal', name: 'Legal Documents', description: 'Legal Notices, Insurance Policies, Litigation Documents' },
    { id: 'meeting', name: 'Meeting Minutes', description: 'Board Meeting Minutes, Annual Meeting Minutes' },
    { id: 'maintenance', name: 'Maintenance Records', description: 'Maintenance Contracts, Service Records' }
  ];

  // Permission types
  const permissionTypes = [
    { id: 'all', name: 'All Homeowners', description: 'Visible to all community members' },
    { id: 'board', name: 'Board Members Only', description: 'Restricted to board members' },
    { id: 'specific', name: 'Specific Users', description: 'Selected users only' }
  ];

  // Load storage usage
  const loadStorageUsage = async () => {
    try {
      setLoadingStorage(true);
      const response = await api.get('/api/documents/storage-stats');
      // Transform response to match expected format
      setStorageUsage({
        used_gb: response.data.total_gb,
        limit_gb: response.data.max_gb,
        percentage_used: (response.data.total_gb / response.data.max_gb) * 100,
        is_over_limit: response.data.total_gb > response.data.max_gb,
        is_approaching_limit: response.data.total_gb > (response.data.max_gb * 0.8),
        document_count: response.data.final_documents
      });
    } catch (error) {
      console.error('Error loading storage usage:', error);
    } finally {
      setLoadingStorage(false);
    }
  };

  // Load document review period setting
  const loadDocumentReviewSetting = async () => {
    setLoadingReviewSetting(true);
    try {
      console.log('📥 Loading document review setting...');
      const response = await api.get('/api/hoa/settings/document-policy');
      console.log('✅ Loaded setting:', response.data);
      setDocumentReviewDays(response.data.document_review_days);
    } catch (error) {
      console.error('❌ Error loading document review setting:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
    } finally {
      setLoadingReviewSetting(false);
    }
  };

  const saveDocumentReviewSetting = async (days) => {
    setSavingReviewSetting(true);
    try {
      console.log(`💾 Saving document review setting: ${days} days`);
      const response = await api.put('/api/hoa/settings/document-policy', {
        document_review_days: days
      });
      console.log('✅ Save response:', response.data);
      setDocumentReviewDays(days);
      alert(`✅ Document review period updated to ${days === 0 ? 'immediate (0 days)' : '3 days'}`);
    } catch (error) {
      console.error('❌ Error saving document review setting:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
      alert(`❌ Failed to save setting. ${error.response?.data?.detail || 'Please try again.'}`);
    } finally {
      setSavingReviewSetting(false);
    }
  };

  // Load documents and users on component mount
  useEffect(() => {
    loadDocuments();
    loadAvailableUsers();
    loadDocumentSettings();
    loadFolders(); // Load HOA Files Pro folders
    loadStorageUsage(); // Load storage usage
    loadDocumentReviewSetting(); // Load review period setting
  }, []);

  // Debug: Log document settings and user permission check
  useEffect(() => {
    console.log('🔐 Document Settings Updated:', {
      allowHomeownerUpload: documentSettings.allowHomeownerUpload,
      allowedUploadUsers: documentSettings.allowedUploadUsers,
      currentUserId: safeUser.id,
      isInAllowedList: documentSettings.allowedUploadUsers?.includes(safeUser.id),
      canUpload: canUploadDocuments()
    });
  }, [documentSettings, safeUser.id]);

  const loadDocuments = async () => {
    try {
      // Map frontend category IDs to backend enum values
      const categoryEnumMap = {
        'meeting': 'meeting_minutes',
        'governing': 'bylaws',
        'financial': 'financial_reports',
        'policies': 'policies',
        'legal': 'other',
        'maintenance': 'other',
        'owner_statements': 'other',
        'bids_contracts': 'other',
        'agendas_notices': 'other',
        'cc_rs': 'cc_rs',
        'arc_documents': 'arc_documents',
        'forms': 'forms',
        'newsletters': 'newsletters',
        'other': 'other'
      };
      
      // Convert frontend filter to backend enum value
      const backendCategory = filterCategory !== 'all' 
        ? categoryEnumMap[filterCategory] 
        : undefined;
      
      // Fetch ONLY from regular documents collection - NOT from HOA Files Pro
      // HOA Files Pro documents should ONLY be accessible through HOA Files Pro tab
      // This prevents password-protected documents from being exposed in regular Documents tab
      const oldDocs = await api.get('/api/documents', { 
        params: { category: backendCategory } 
      }).catch(() => ({ data: { documents: [] } }));
      
      const docs = [...(oldDocs.data?.documents || [])];
      
      // map backend schema to frontend expected fields
      const mapped = docs.map(d => ({
        id: d.id,
        name: d.title || d.file_name || 'Untitled Document',
        title: d.title || d.file_name || 'Untitled Document', // Keep title for search
        // Map backend categories to UI categories
        category: d.category === 'meeting_minutes' ? 'meeting' : 
                 d.category === 'Owner Statements' ? 'owner_statements' : 
                 (d.category || 'other').toLowerCase().replace(/ /g, '_'),
        uploadDate: d.uploaded_at || d.created_at,
        type: d.document_type || d.mime_type || 'application/pdf',
        size: d.size || d.file_size || d.size_bytes,
        file_size: d.size || d.file_size || d.size_bytes,
        viewPermission: d.visibility === 'specific_users' || (d.specific_users && d.specific_users.length > 0) ? 'specific' :
                       d.is_board_only || d.visibility === 'board_only' ? 'board' : 
                       d.tagged_homeowners && d.tagged_homeowners.length > 0 ? 'specific' : 'all',
        // CRITICAL: Preserve original fields for filtering
        is_board_only: d.is_board_only,
        visibility: d.visibility,
        visible_to_all: d.visible_to_all,
        visible_to_board: d.visible_to_board,
        tagged_homeowners: d.tagged_homeowners || [],
        audience: d.audience,
        specificUsers: d.specific_users || d.tagged_homeowners || [],
        uploadedBy: d.uploaded_by || 'Unknown',
        uploadedByUserId: d.uploaded_by,
        filePath: d.file_path,
        fileName: d.file_name,
        tags: d.tags || [],
        hoa_id: d.hoa_id,
        hoa_code: d.hoa_code,
        manual_folder_id: d.manual_folder_id,
        property_address: d.property_address,
        statement_data: d.statement_data
      }));
      setDocuments(mapped);
    } catch (e) {
      console.error('Failed to load documents from API:', e);
      setDocuments([]); // Clear documents on API failure instead of localStorage fallback
      alert('Failed to load documents. Please refresh the page.');
    }
  };

  const loadDocumentSettings = async () => {
    try {
      const response = await api.get('/api/hoa/settings/document-policy');
      const data = response.data;
      
      setDocumentSettings({
        allowHomeownerDelete: data.allowHomeownerDelete || false,
        allowHomeownerUpload: data.allowHomeownerUpload || false,
        allowSecurityUpload: data.allowSecurityUpload || false,
        allowMaintenanceUpload: data.allowMaintenanceUpload || false,
        allowedUploadUsers: data.allowedUploadUsers || []
      });
      
      console.log('✅ Loaded document settings:', data);
    } catch (error) {
      console.error('Error loading document settings:', error);
      // Set defaults on error
      setDocumentSettings({
        allowHomeownerDelete: false,
        allowHomeownerUpload: false,
        allowSecurityUpload: false,
        allowMaintenanceUpload: false,
        allowedUploadUsers: []
      });
    }
  };

  const saveDocumentSettings = async (newSettings) => {
    try {
      console.log('💾 Saving document settings:', newSettings);
      
      await api.put('/api/hoa/settings/document-policy', {
        document_permissions: newSettings
      });
      
      setDocumentSettings(newSettings);
      console.log('✅ Document settings saved successfully');
    } catch (error) {
      console.error('❌ Error saving document settings:', error);
      alert('Failed to save document settings. Please try again.');
    }
  };

  const loadAvailableUsers = async () => {
    try {
      // Fetch HOA members, team members (staff), and team users (maintenance/security)
      const hoaMembersPromise = api.get('/api/hoa/members').catch(() => ({ data: { members: [] } }));
      const teamMembersPromise = api.get('/api/team-members').catch(() => ({ data: { team_members: [] } }));
      
      // Get HOA ID for team users query
      let teamUsersPromise = Promise.resolve({ data: { team_users: [] } });
      if (safeUser.hoa_id) {
        teamUsersPromise = api.get(`/api/team-users?hoa_id=${safeUser.hoa_id}`).catch(() => ({ data: { team_users: [] } }));
      }
      
      const [membersResponse, teamMembersResponse, teamUsersResponse] = await Promise.all([
        hoaMembersPromise,
        teamMembersPromise, 
        teamUsersPromise
      ]);
      
      const hoaMembers = membersResponse.data?.members || [];
      const teamMembers = teamMembersResponse.data?.team_members || [];
      const teamUsers = teamUsersResponse.data?.team_users || [];
      
      console.log(`📊 Loaded users: ${hoaMembers.length} HOA members, ${teamMembers.length} team members, ${teamUsers.length} team users (maintenance/security)`);
      
      // Combine all three lists
      const allMembers = [...hoaMembers, ...teamMembers, ...teamUsers];
      
      // Fetch manual folders
      let manualFolders = [];
      try {
        const foldersResponse = await api.get('/api/hoa-files-pro/manual-folders');
        manualFolders = foldersResponse.data?.folders || [];
      } catch (err) {
        console.log('Could not load manual folders (may not have access)');
      }
      
      // Process and group users
      const processedUsers = allMembers.map(member => {
        let userType = 'homeowner'; // default
        let displayRole = member.role || 'homeowner';
        
        // Determine user type based on role and external_group
        if (['president', 'vice_president', 'treasurer', 'secretary', 'board_member'].includes(member.role)) {
          userType = 'board_member';
          displayRole = member.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
        } else if (member.role === 'maintenance') {
          userType = 'maintenance';
          displayRole = 'Maintenance Team';
        } else if (member.role === 'security') {
          userType = 'security';
          displayRole = 'Security Team';
        } else if (member.external_group) {
          userType = member.external_group;
          displayRole = member.external_group.replace(/\b\w/g, l => l.toUpperCase());
        }
        
        return {
          id: member.id,
          name: member.name,
          email: member.email,
          type: userType,
          role: displayRole,
          external_group: member.external_group
        };
      });
      
      // Add manual folders as "users"
      const folderUsers = manualFolders.map(folder => ({
        id: folder.id,
        name: `📁 ${folder.address}`,
        email: folder.notes || 'Manual Folder (No Account)',
        type: 'manual_folder',
        role: 'Manual Folder',
        isManualFolder: true
      }));
      
      setAvailableUsers([...processedUsers, ...folderUsers]);
      console.log(`Loaded ${processedUsers.length} users + ${folderUsers.length} manual folders for document access management`);
    } catch (error) {
      console.error('Failed to load users:', error);
      // Fallback to empty array if API fails
      setAvailableUsers([]);
    }
  };

  const loadFolders = async () => {
    try {
      const response = await api.get('/api/hoa-files-pro/all-folders');
      const folders = response.data?.folders || [];
      setAvailableFolders(folders);
      console.log(`Loaded ${folders.length} folders (properties + manual) for HOA Files Pro tagging`);
    } catch (error) {
      console.error('Failed to load folders:', error);
      setAvailableFolders([]);
    }
  };


  // Filter and search documents
  const filteredDocuments = documents.filter(doc => {
    // Role-based visibility
    if (safeUser.role === 'homeowner' && (doc.is_board_only || doc.visibility === 'board_only')) {
      return false;
    }
    if (safeUser.role === 'homeowner' && doc.visibility === 'specific_users' && 
        !doc.specific_users?.some(userId => userId === safeUser.id)) {
      return false;
    }

    // Search filter - check both title and name fields
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchesTitle = doc.title?.toLowerCase().includes(term);
      const matchesName = doc.name?.toLowerCase().includes(term);
      const matchesFileName = doc.fileName?.toLowerCase().includes(term);
      if (!matchesTitle && !matchesName && !matchesFileName) {
        return false;
      }
    }

    // Category filter
    if (filterCategory !== 'all' && doc.category !== filterCategory) {
      return false;
    }

    // Visibility filter
    if (filterVisibility !== 'all') {
      if (filterVisibility === 'homeowners') {
        // Show docs that are NOT board-only (i.e., accessible to homeowners)
        if (doc.is_board_only === true || doc.visibility === 'board_only') {
          return false;
        }
        // Also exclude specific users docs
        if (doc.visibility === 'specific_users' || doc.visibility === 'board_and_signer') {
          return false;
        }
      } else if (filterVisibility === 'board') {
        // Show only board-only docs
        if (doc.is_board_only !== true && doc.visibility !== 'board_only') {
          return false;
        }
      } else if (filterVisibility === 'specific') {
        // Show only specific users docs (including board_and_signer)
        if (doc.visibility !== 'specific_users' && doc.visibility !== 'board_and_signer') {
          return false;
        }
      }
    }

    // Date range filter
    if (filterDateFrom || filterDateTo) {
      const docDate = new Date(doc.uploadDate);
      if (filterDateFrom && docDate < new Date(filterDateFrom)) {
        return false;
      }
      if (filterDateTo && docDate > new Date(filterDateTo + 'T23:59:59')) {
        return false;
      }
    }

    // Uploaded by filter
    if (filterUploadedBy) {
      const term = filterUploadedBy.toLowerCase();
      const uploadedBy = (doc.uploadedBy || '').toLowerCase();
      if (!uploadedBy.includes(term)) {
        return false;
      }
    }

    return true;
  });

  console.log(`🔍 FILTER DEBUG: filterVisibility="${filterVisibility}", showing ${filteredDocuments.length}/${documents.length} documents`);

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    handleBulkUpload(files);
  };

  // Bulk upload handler - Using backend API for persistence
  const handleBulkUpload = async (files) => {
    if (files.length === 0) return;

    setUploadingDocuments(true);
    const newProgress = {};
    let successCount = 0;
    let errorCount = 0;
    
    try {
      // Process files sequentially to avoid overwhelming the server
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileId = `${Date.now()}_${i}`;
        newProgress[fileId] = 0;
        setUploadProgress({...newProgress});

        try {
          // Create FormData for file upload
          const formData = new FormData();
          formData.append('file', file);
          formData.append('title', file.name);
          formData.append('category', selectedCategory === 'meeting' ? 'meeting_minutes' : selectedCategory);
          formData.append('is_board_only', selectedVisibility === 'board' ? 'true' : 'false');
          
          // Add specific users if selected (ONLY for specific visibility)
          if (selectedVisibility === 'specific' && specificUsers.length > 0) {
            formData.append('specific_users', JSON.stringify(specificUsers));
          }
          
          // Add HOA Files Pro folder IDs separately (does NOT affect visibility)
          if (selectedFolders.length > 0 && selectedVisibility !== 'all') {
            formData.append('folder_ids', JSON.stringify(selectedFolders));
          }

          // Upload file via backend API
          const response = await api.post('/api/board/documents/upload', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (progressEvent) => {
              const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
              newProgress[fileId] = progress;
              setUploadProgress(prevProgress => ({...prevProgress, [fileId]: progress}));
            }
          });

          if (response.status === 200) {
            successCount++;
            newProgress[fileId] = 100;
            setUploadProgress(prevProgress => ({...prevProgress, [fileId]: 100}));
          } else {
            errorCount++;
            console.error(`Failed to upload ${file.name}:`, response);
          }

        } catch (fileError) {
          errorCount++;
          console.error(`Error uploading ${file.name}:`, fileError);
          newProgress[fileId] = 0; // Mark as failed
          setUploadProgress(prevProgress => ({...prevProgress, [fileId]: 0}));
        }
      }

      // Reload documents from backend to show uploaded files
      await loadDocuments();

      // Show results
      if (successCount > 0 && errorCount === 0) {
        alert(`✅ All ${successCount} document(s) uploaded successfully!`);
      } else if (successCount > 0 && errorCount > 0) {
        alert(`⚠️ ${successCount} document(s) uploaded successfully, ${errorCount} failed. Please check console for details.`);
      } else {
        alert(`❌ Failed to upload documents. Please try again.`);
      }

      // Reset form
      setSpecificUsers([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (error) {
      console.error('Error during bulk upload:', error);
      alert('❌ Error uploading documents. Please try again.');
    } finally {
      setUploadingDocuments(false);
      setUploadProgress({});
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    handleBulkUpload(files);
  };

  // Document management handlers
  const startEditing = (docId) => {
    const doc = documents.find(d => d.id === docId);
    setIsEditing(docId);
    setEditData({
      name: doc.name,
      category: doc.category,
      viewPermission: doc.viewPermission,
      specificUsers: doc.specificUsers || []
    });
  };

  const saveEdit = async (docId) => {
    try {
      const doc = documents.find(d => d.id === docId);
      const payload = {
        id: docId,
        title: editData.name || doc.name,
        category: editData.category || doc.category,
        is_board_only: (editData.viewPermission || doc.viewPermission) === 'board',
        specific_users: editData.specificUsers || doc.specificUsers || []
      };
      await api.put(`/api/board/documents/${docId}`, payload);
      await loadDocuments();
      setIsEditing(null);
      setEditData({});
    } catch (e) {
      console.error('Failed to save document edit', e);
      alert('Failed to save changes');
    }
  };

  const cancelEdit = () => {
    setIsEditing(null);
    setEditData({});
  };

  // Modal state for removal request
  const [showRemovalModal, setShowRemovalModal] = useState(false);
  const [removalDocId, setRemovalDocId] = useState(null);
  const [removalReason, setRemovalReason] = useState('');
  
  // Modal state for replace document
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [replaceDocId, setReplaceDocId] = useState(null);
  const [replaceDoc, setReplaceDoc] = useState(null);
  const [replaceFile, setReplaceFile] = useState(null);
  const [replaceNotes, setReplaceNotes] = useState('');

  const requestDocumentRemoval = async (docId) => {
    setRemovalDocId(docId);
    setRemovalReason('');
    setShowRemovalModal(true);
  };

  const submitRemovalRequest = async () => {
    if (!removalReason || removalReason.trim().length < 10) {
      alert('Please provide a detailed reason (at least 10 characters) for removing this document.');
      return;
    }

    try {
      await api.post(`/api/documents/${removalDocId}/request-removal`, {
        reason: removalReason.trim()
      });
      
      alert('✅ Document removal request submitted successfully. An administrator will review your request.');
      setShowRemovalModal(false);
      setRemovalDocId(null);
      setRemovalReason('');
      
      // Reload documents to show updated status
      await loadDocuments();
    } catch (error) {
      console.error('Failed to request document removal:', error);
      alert('❌ Failed to submit removal request. Please try again.');
    }
  };

  const openReplaceModal = (doc) => {
    setReplaceDocId(doc.id);
    setReplaceDoc(doc);
    setReplaceFile(null);
    setReplaceNotes('');
    setShowReplaceModal(true);
  };

  const submitReplaceDocument = async () => {
    if (!replaceFile) {
      alert('Please select a file to replace with.');
      return;
    }

    try {
      // Use the new versioning API to replace document
      // This creates a new version and archives the old one for audit trail
      const formData = new FormData();
      formData.append('file', replaceFile);
      if (replaceNotes) {
        formData.append('notes', replaceNotes);
      }
      
      console.log(`📝 Replacing document ${replaceDocId} with versioning...`);
      await api.post(`/api/documents/${replaceDocId}/replace`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      alert('✅ Document replaced successfully! Previous version kept for audit trail.');
      setShowReplaceModal(false);
      setReplaceDocId(null);
      setReplaceDoc(null);
      setReplaceFile(null);
      setReplaceNotes('');
      
      // Reload documents
      await loadDocuments();
    } catch (error) {
      console.error('❌ Failed to replace document:', error);
      alert('❌ Failed to replace document. ' + (error.response?.data?.detail || 'Please try again.'));
    }
  };

  const deleteDocument = async (docId) => {
    // Find the document to check its status
    const doc = documents.find(d => d.id === docId);
    
    // If document is in "final" status, show message about contacting support
    if (doc && doc.status === 'final') {
      const confirmAction = window.confirm(
        '📋 Document Removal Request\n\n' +
        'This document has passed the 3-day draft review period and is now part of the official record.\n\n' +
        'Due to legal document retention requirements (7-year minimum), it cannot be deleted directly.\n\n' +
        '⚠️ To request removal, you must submit a support ticket:\n\n' +
        'Document Information:\n' +
        '• Document ID: ' + docId + '\n' +
        '• Document Name: ' + doc.name + '\n\n' +
        'Click OK to go to Support Tickets tab to submit your removal request.'
      );
      
      if (confirmAction) {
        // Try to trigger navigation to support tickets tab
        const event = new CustomEvent('navigate-to-support', { 
          detail: { 
            documentId: docId, 
            documentName: doc.name 
          } 
        });
        window.dispatchEvent(event);
      }
      return;
    }

    // For draft documents or legacy documents without status, allow direct deletion
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        // Call backend API to delete document
        await api.delete(`/api/board/documents/${docId}`);
        
        // Reload documents from backend to ensure sync
        await loadDocuments();
        
        alert('✅ Document deleted successfully!');
      } catch (error) {
        console.error('Failed to delete document:', error);
        const errorMsg = error.response?.data?.detail || 'Failed to delete document. Please try again.';
        alert('❌ ' + errorMsg);
      }
    }
  };

  // Filter state for mass download
  const [massDownloadFilters, setMassDownloadFilters] = useState({
    dateFrom: '',
    dateTo: '',
    category: 'all',
    nameSearch: ''
  });
  const [showMassDownloadModal, setShowMassDownloadModal] = useState(false);

  // Get filtered documents for mass download
  const getFilteredDocumentsForDownload = () => {
    let filtered = documents;
    
    // Filter by date range
    if (massDownloadFilters.dateFrom) {
      filtered = filtered.filter(doc => {
        const docDate = new Date(doc.uploadedAt || doc.created_at);
        return docDate >= new Date(massDownloadFilters.dateFrom);
      });
    }
    
    if (massDownloadFilters.dateTo) {
      filtered = filtered.filter(doc => {
        const docDate = new Date(doc.uploadedAt || doc.created_at);
        return docDate <= new Date(massDownloadFilters.dateTo);
      });
    }
    
    // Filter by category
    if (massDownloadFilters.category !== 'all') {
      filtered = filtered.filter(doc => doc.category === massDownloadFilters.category);
    }
    
    // Filter by name search
    if (massDownloadFilters.nameSearch) {
      const searchLower = massDownloadFilters.nameSearch.toLowerCase();
      filtered = filtered.filter(doc => 
        (doc.name || '').toLowerCase().includes(searchLower) ||
        (doc.title || '').toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  };

  // Mass download with filters
  const massDownloadFiltered = async () => {
    const docsToDownload = getFilteredDocumentsForDownload();
    
    if (docsToDownload.length === 0) {
      alert('No documents match the selected filters');
      return;
    }

    if (!window.confirm(`Download ${docsToDownload.length} document(s)?`)) {
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const doc of docsToDownload) {
      try {
        // Backend now returns JSON with presigned URL
        const response = await api.get(`/api/documents/${doc.id}/download`, {
          headers: {
            'X-HOA-ID': user?.hoa_id
          }
        });
        
        if (response.data && response.data.url) {
          // Open presigned URL to trigger download
          const link = document.createElement('a');
          link.href = response.data.url;
          link.target = '_blank';
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          successCount++;
        } else {
          throw new Error('Invalid response format');
        }
        
        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error(`Failed to download ${doc.name}:`, error);
        failCount++;
      }
    }
    
    alert(`✅ Downloaded ${successCount} document(s)${failCount > 0 ? `, ${failCount} failed` : ''}`);
    setShowMassDownloadModal(false);
    setMassDownloadFilters({ dateFrom: '', dateTo: '', category: 'all', nameSearch: '' });
  };

  // Mass download function (original - for selected documents)
  const handleMassDownload = async () => {
    if (selectedDocuments.length === 0) {
      alert('Please select at least one document to download');
      return;
    }

    if (!window.confirm(`Download ${selectedDocuments.length} document(s)?`)) {
      return;
    }

    for (const docId of selectedDocuments) {
      const doc = documents.find(d => d.id === docId);
      if (!doc) continue;

      try {
        // Backend now returns JSON with presigned URL
        const response = await api.get(`/api/documents/${docId}/download`);
        
        if (response.data && response.data.url) {
          // Open presigned URL to trigger download
          const link = document.createElement('a');
          link.href = response.data.url;
          link.target = '_blank';
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          throw new Error('Invalid response format');
        }
        
        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error(`Failed to download ${doc.name}:`, error);
      }
    }
    
    alert(`✅ Downloaded ${selectedDocuments.length} document(s)`);
    setSelectedDocuments([]);
  };

  // Select all documents
  const handleSelectAll = () => {
    if (selectedDocuments.length === filteredDocuments.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(filteredDocuments.map(d => d.id));
    }
  };


  // User selection handlers
  const addSpecificUser = (userId) => {
    if (!specificUsers.includes(userId)) {
      setSpecificUsers([...specificUsers, userId]);
    }
    setUserSearchTerm('');
  };

  const removeSpecificUser = (userId) => {
    setSpecificUsers(specificUsers.filter(id => id !== userId));
  };

  const addEditUser = (userId) => {
    if (!editData.specificUsers?.includes(userId)) {
      setEditData({
        ...editData,
        specificUsers: [...(editData.specificUsers || []), userId]
      });
    }
  };

  const removeEditUser = (userId) => {
    setEditData({
      ...editData,
      specificUsers: editData.specificUsers?.filter(id => id !== userId) || []
    });
  };

  // View and download handlers
  const handleViewDocument = async (doc) => {
    console.log('🔵 VIEW BUTTON CLICKED');
    console.log('📄 Document:', doc);
    console.log('🆔 Document ID:', doc.id);
    
    // Detect if mobile (iOS, Android)
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    console.log('📱 Mobile device detected:', isMobile);
    
    try {
      console.log('🔄 Making API request to:', `/api/documents/${doc.id}/view`);
      const resp = await api.get(`/api/documents/${doc.id}/view`, { responseType: 'blob' });
      
      console.log('✅ API Response received');
      console.log('📦 Response type:', resp.data instanceof Blob ? 'Blob' : 'JSON');
      
      // NEW DOCUMENTS: Backend returns JSON with presigned URL
      if (resp.data instanceof Blob && resp.data.type === 'application/json') {
        const text = await resp.data.text();
        const jsonData = JSON.parse(text);
        if (jsonData.url) {
          console.log('✅ Got presigned URL (NEW document)');
          
          if (isMobile) {
            // Mobile: Navigate in same tab to avoid popup blocker
            console.log('📱 Mobile: Navigating in same tab');
            window.location.href = jsonData.url;
          } else {
            // Desktop: Try to open in new window
            console.log('💻 Desktop: Opening in new window');
            const newWindow = window.open(jsonData.url, '_blank', 'noopener,noreferrer');
            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
              // Fallback: navigate in same tab if popup blocked
              console.log('⚠️ Popup blocked, using same tab');
              window.location.href = jsonData.url;
            }
          }
          return;
        }
      }
      
      // OLD DOCUMENTS: Backend returns file directly (FileResponse)
      console.log('✅ Got file directly (OLD document)');
      const blob = resp.data instanceof Blob ? resp.data : new Blob([resp.data]);
      const objectUrl = URL.createObjectURL(blob);
      
      if (isMobile) {
        // Mobile: Navigate in same tab
        console.log('📱 Mobile: Navigating in same tab');
        window.location.href = objectUrl;
      } else {
        // Desktop: Try to open in new window
        console.log('💻 Desktop: Opening in new window');
        const newWindow = window.open(objectUrl, '_blank', 'noopener,noreferrer');
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          // Fallback: navigate in same tab if popup blocked
          console.log('⚠️ Popup blocked, using same tab');
          window.location.href = objectUrl;
        } else {
          setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
        }
      }
    } catch (error) {
      console.error('❌❌❌ VIEW FAILED ❌❌❌');
      console.error('Error object:', error);
      
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to view document';
      alert(`Error viewing document: ${errorMsg}`);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const resp = await api.get(`/api/documents/${doc.id}/download`, { responseType: 'blob' });
      
      // NEW DOCUMENTS: Backend returns JSON with presigned URL
      if (resp.data instanceof Blob && resp.data.type === 'application/json') {
        const text = await resp.data.text();
        const jsonData = JSON.parse(text);
        if (jsonData.url) {
          console.log('✅ Got presigned download URL (NEW document)');
          const a = document.createElement('a');
          a.href = jsonData.url;
          a.target = '_blank';
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          return;
        }
      }
      
      // OLD DOCUMENTS: Backend returns file directly (FileResponse)
      console.log('✅ Got file directly (OLD document)');
      const blob = resp.data instanceof Blob ? resp.data : new Blob([resp.data]);
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = doc.fileName || doc.name || 'document';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      console.error('Download failed', e);
      const errorMsg = e.response?.data?.detail || e.message || 'Download failed';
      if (errorMsg.includes('document may need to be regenerated')) {
        alert('⚠️ This document appears to be corrupted or incomplete. It may need to be regenerated. Please contact an administrator.');
      } else {
        alert(`Download failed: ${errorMsg}`);
      }
    }
  };

  // Get user display name with group info
  const getUserDisplayName = (userId) => {
    const user = availableUsers.find(u => u.id === userId);
    if (!user) return 'Unknown User';
    
    const groupInfo = user.external_group ? ` [${user.external_group}]` : '';
    return `${user.name}${groupInfo}`;
  };

  // Filter users for search - show all users when no search term
  const filteredUsers = availableUsers.filter(user => {
    if (!userSearchTerm) return true; // Show all users when no search term
    
    const searchLower = userSearchTerm.toLowerCase();
    return user.name.toLowerCase().includes(searchLower) ||
           user.email.toLowerCase().includes(searchLower) ||
           user.type.toLowerCase().includes(searchLower);
  });

  const isBoardMember = safeUser.role === 'board_member' || safeUser.role === 'president' || 
                       safeUser.role === 'vice_president' || safeUser.role === 'treasurer' || 
                       safeUser.role === 'secretary';

  // Check if user can delete a specific document
  const canDeleteDocument = (document) => {
    // Board members can always delete any document
    if (isBoardMember) {
      return true;
    }
    
    // Check if user is in specific allowed users list - they can delete their own uploads
    if (documentSettings.allowedUploadUsers && 
        documentSettings.allowedUploadUsers.includes(safeUser.id) &&
        document.uploadedByUserId === safeUser.id) {
      return true;
    }
    
    // Homeowners can only delete their own documents if setting allows
    if (safeUser.role === 'homeowner' && documentSettings.allowHomeownerDelete) {
      return document.uploadedByUserId === safeUser.id;
    }
    
    // Other roles: maintenance/security can delete their own documents if settings allow
    if ((safeUser.role === 'security_team' && documentSettings.allowSecurityUpload) ||
        (safeUser.role === 'maintenance_team' && documentSettings.allowMaintenanceUpload)) {
      return document.uploadedByUserId === safeUser.id;
    }
    
    return false;
  };

  // Check if user can upload documents
  const canUploadDocuments = () => {
    // Board members always have upload permission
    if (isBoardMember) return true;
    
    // Check if user is in the specific allowed users list
    if (documentSettings.allowedUploadUsers && 
        documentSettings.allowedUploadUsers.includes(safeUser.id)) {
      console.log(`✅ User ${safeUser.id} has specific upload permission`);
      return true;
    }
    
    // Check global role-based permissions
    if (safeUser.role === 'homeowner' && documentSettings.allowHomeownerUpload) return true;
    if (safeUser.role === 'security_team' && documentSettings.allowSecurityUpload) return true;
    if (safeUser.role === 'maintenance_team' && documentSettings.allowMaintenanceUpload) return true;
    
    return false;
  };

  // Sub-tab state
  const [currentSubTab, setCurrentSubTab] = useState('documents');

  return (
    <div className="space-y-6">
      {/* Sub-tabs Navigation */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-3 border-b">
          <nav className="flex space-x-8">
            <button
              onClick={() => setCurrentSubTab('documents')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                currentSubTab === 'documents'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📁 Document Library
            </button>
            {/* SMART PRO TAB - TEMPORARILY HIDDEN FOR DEPLOYMENT
            <button
              onClick={() => setCurrentSubTab('smart-pro')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                currentSubTab === 'smart-pro'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              🧠 HOA Smart Pro
            </button>
            */}
            {['board_member', 'president', 'vice_president', 'treasurer', 'secretary', 'platform_admin'].includes(safeUser.role) && (
              <button
                onClick={() => setCurrentSubTab('settings')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  currentSubTab === 'settings'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                ⚙️ Settings
              </button>
            )}
          </nav>
        </div>
      </div>

      {/* Render current sub-tab content */}
      {/* SMART PRO - TEMPORARILY DISABLED FOR DEPLOYMENT
      currentSubTab === 'smart-pro' ? (
        <SmartPro user={safeUser} />
      ) : */}
      {currentSubTab === 'settings' ? (
        <DocumentSettings 
          documentSettings={documentSettings}
          setDocumentSettings={setDocumentSettings}
          saveDocumentSettings={saveDocumentSettings}
          documentReviewDays={documentReviewDays}
          loadingReviewSetting={loadingReviewSetting}
          savingReviewSetting={savingReviewSetting}
          saveDocumentReviewSetting={saveDocumentReviewSetting}
          availableUsers={availableUsers}
        />
      ) : (
        <div className="documents-content">
          {/* Popup Blocker Notice */}
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  <strong>Note:</strong> When viewing documents, your browser may initially block popups. If a document doesn't open, please allow popups for this site and try again.
                </p>
              </div>
            </div>
          </div>
          
          {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search Documents</label>
              <input
                type="text"
                placeholder="Search by document name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Category</label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">All Categories</option>
                {documentCategories.map(category => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Visibility</label>
              <select
                value={filterVisibility}
                onChange={(e) => setFilterVisibility(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">All Permissions</option>
                <option value="homeowners">All Homeowners</option>
                <option value="board">Board Members Only</option>
                <option value="specific">Specific Users</option>
              </select>
            </div>
            <div className="flex items-end">
              <div className="text-sm text-gray-600">
                {filteredDocuments.length} of {documents.length} documents
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Storage Usage Widget */}
      {storageUsage && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center mb-2">
              <span className="font-medium text-gray-900">Cloud Storage</span>
              <span className="text-sm text-gray-600">
                {storageUsage.used_gb.toFixed(2)} GB / {storageUsage.limit_gb.toFixed(1)} GB
              </span>
            </div>
            <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${
                  storageUsage.is_over_limit ? 'bg-red-500'
                  : storageUsage.percentage_used >= 80 ? 'bg-orange-400'
                  : 'bg-emerald-500'
                }`}
                style={{ width: `${Math.min(100, storageUsage.percentage_used)}%` }}
              />
            </div>
            {storageUsage.is_approaching_limit && (
              <p className="mt-2 text-xs text-orange-700">
                ⚠️ You're approaching your storage limit. Consider archiving older documents or contact support to increase your storage capacity.
              </p>
            )}
            {storageUsage.is_over_limit && (
              <p className="mt-2 text-xs text-red-700">
                🚫 Storage limit exceeded! You cannot upload new documents until space is freed or your limit is increased.
              </p>
            )}
            <div className="mt-2 text-xs text-gray-500">
              {storageUsage.document_count} final documents stored • Complies with 7-year retention requirement
            </div>
          </div>
        </div>
      )}

      {/* Document Management Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Document Management</h3>
            <div className="flex gap-3">
              <button
                onClick={() => setShowMassDownloadModal(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium flex items-center gap-2"
              >
                <span>📥</span>
                <span>Mass Download</span>
              </button>
              
              {selectedDocuments.length > 0 && (
                <button
                  onClick={handleMassDownload}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium flex items-center gap-2"
                >
                  <span>📥</span>
                  <span>Download Selected ({selectedDocuments.length})</span>
                </button>
              )}
            </div>
          </div>
          
          {filteredDocuments.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="mt-2">No documents found</p>
              <p className="text-sm text-gray-400">
                {safeUser?.role === 'homeowner' 
                  ? 'No documents match your search criteria or have been shared with homeowners.'
                  : 'Upload documents or adjust your search criteria.'
                }
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visibility</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Upload Info</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDocuments.map(document => (
                    <tr key={document.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        {isEditing === document.id ? (
                          <input
                            type="text"
                            value={editData.name}
                            onChange={(e) => setEditData({...editData, name: e.target.value})}
                            className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        ) : (
                          <div>
                            <button 
                              onClick={() => handleViewDocument(document)}
                              className="text-sm font-medium text-gray-900 cursor-pointer hover:text-indigo-600 underline text-left"
                            >
                              {document.name || document.title}
                            </button>
                            <div className="text-sm text-gray-500">
                              {(() => {
                                const size = document.size || document.file_size || document.size_bytes;
                                return size ? `${Math.round(size / 1024)} KB` : 'Unknown size';
                              })()}
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {isEditing === document.id ? (
                          <select
                            value={editData.category}
                            onChange={(e) => setEditData({...editData, category: e.target.value})}
                            className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                          >
                            {documentCategories.map(category => (
                              <option key={category.id} value={category.id}>{category.name}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-sm text-gray-900">
                            {documentCategories.find(cat => cat.id === document.category)?.name}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {isEditing === document.id ? (
                          <div className="space-y-2">
                            <select
                              value={editData.viewPermission}
                              onChange={(e) => setEditData({...editData, viewPermission: e.target.value})}
                              className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                            >
                              {permissionTypes.map(type => (
                                <option key={type.id} value={type.id}>{type.name}</option>
                              ))}
                            </select>
                            
                            {editData.viewPermission === 'specific' && (
                              <div>
                                <input
                                  type="text"
                                  placeholder="Search users..."
                                  value={userSearchTerm}
                                  onChange={(e) => setUserSearchTerm(e.target.value)}
                                  className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500 text-xs"
                                />
                                {userSearchTerm && (
                                  <div className="mt-1 max-h-32 overflow-y-auto border border-gray-200 rounded">
                                    {filteredUsers.map(user => (
                                      <button
                                        key={user.id}
                                        onClick={() => addEditUser(user.id)}
                                        className="w-full text-left px-2 py-1 hover:bg-gray-50 text-xs"
                                      >
                                        {user.name} ({user.email})
                                      </button>
                                    ))}
                                  </div>
                                )}
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {editData.specificUsers?.map(userId => (
                                    <span
                                      key={userId}
                                      className="inline-flex items-center px-1 py-0.5 rounded text-xs bg-indigo-100 text-indigo-800"
                                    >
                                      {availableUsers.find(u => u.id === userId)?.name}
                                      <button
                                        onClick={() => removeEditUser(userId)}
                                        className="ml-1 text-indigo-600 hover:text-indigo-900"
                                      >
                                        ×
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              document.viewPermission === 'board' ? 'bg-red-100 text-red-800' :
                              document.viewPermission === 'specific' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {permissionTypes.find(p => p.id === document.viewPermission)?.name}
                            </span>
                            {/* Quick visibility toggle */}
                            {isBoardMember && (
                              <select
                                value={document.viewPermission}
                                onChange={async (e) => {
                                  try {
                                    const newPerm = e.target.value;
                                    await api.put(`/api/board/documents/${document.id}`, {
                                      title: document.name,
                                      category: document.category === 'meeting' ? 'meeting_minutes' : document.category,
                                      is_board_only: newPerm === 'board',
                                      specific_users: newPerm === 'specific' ? (document.specificUsers || []) : []
                                    });
                                    await loadDocuments();
                                  } catch (err) { console.error('Failed to toggle visibility', err); alert('Failed to update visibility'); }
                                }}
                                className="text-xs border border-gray-300 rounded px-2 py-0.5"
                              >
                                {permissionTypes.map(t => (
                                  <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                              </select>
                            )}
                            {document.viewPermission === 'specific' && document.specificUsers?.length > 0 && (
                              <div className="text-xs text-gray-500 mt-1">
                                {document.specificUsers.length} selected user(s)
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div>{new Date(document.uploadDate).toLocaleDateString()}</div>
                        <div className="text-xs">{document.uploadedBy || 'Unknown'}</div>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium">
                        <div className="flex space-x-2">
                          {isEditing === document.id ? (
                            <>
                              <button
                                onClick={() => saveEdit(document.id)}
                                className="text-green-600 hover:text-green-900"
                              >
                                Save
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="text-gray-600 hover:text-gray-900"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleViewDocument(document)}
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                View
                              </button>
                              <button
                                onClick={() => handleDownloadDocument(document)}
                                className="text-green-600 hover:text-green-900"
                              >
                                Download
                              </button>
                              {isBoardMember && (
                                <>
                                  <button
                                    onClick={() => startEditing(document.id)}
                                    className="text-blue-600 hover:text-blue-900"
                                  >
                                    Edit
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => openReplaceModal(document)}
                                className="text-blue-600 hover:text-blue-900 mr-3"
                              >
                                Replace
                              </button>
                              {canDeleteDocument(document) && (
                                <button
                                  onClick={() => deleteDocument(document.id)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  {document.status === 'final' ? 'Request Removal' : 'Delete'}
                                </button>
                              )}
                              {document.removal_requested && (
                                <span className="text-xs text-orange-600 font-medium ml-2">
                                  ⏳ Removal Pending
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Document Categories Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {documentCategories.map(category => {
          const categoryDocs = filteredDocuments.filter(doc => doc.category === category.id);
          return (
            <div key={category.id} className="bg-white shadow rounded-lg p-4">
              <h5 className="text-sm font-medium text-gray-900 mb-2">{category.name}</h5>
              <p className="text-xs text-gray-600 mb-3">{category.description}</p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  {categoryDocs.length} document(s)
                </span>
                <button
                  onClick={() => setFilterCategory(category.id)}
                  className="text-xs text-indigo-600 hover:text-indigo-900"
                >
                  View All
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* User Group Management - Board Members Only */}
      {isBoardMember && (
        <UserGroupManagement 
          availableUsers={availableUsers} 
          onUserGroupUpdate={loadAvailableUsers}
        />
      )}

      {/* Mass Download Modal with Filters */}
      {showMassDownloadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">📥 Mass Download with Filters</h3>
            
            <div className="space-y-4 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                  <input
                    type="date"
                    value={massDownloadFilters.dateFrom}
                    onChange={(e) => setMassDownloadFilters({...massDownloadFilters, dateFrom: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                  <input
                    type="date"
                    value={massDownloadFilters.dateTo}
                    onChange={(e) => setMassDownloadFilters({...massDownloadFilters, dateTo: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={massDownloadFilters.category}
                  onChange={(e) => setMassDownloadFilters({...massDownloadFilters, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="all">All Categories</option>
                  {documentCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search by Name</label>
                <input
                  type="text"
                  value={massDownloadFilters.nameSearch}
                  onChange={(e) => setMassDownloadFilters({...massDownloadFilters, nameSearch: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Enter document name..."
                />
              </div>
              
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="text-sm font-medium text-blue-900 mb-2">
                  {getFilteredDocumentsForDownload().length} document(s) match your filters
                </div>
                {getFilteredDocumentsForDownload().length > 0 && (
                  <div className="mt-2 max-h-48 overflow-y-auto">
                    <div className="text-xs font-medium text-blue-700 mb-1">Documents to download:</div>
                    <ul className="space-y-1">
                      {getFilteredDocumentsForDownload().map((doc, idx) => (
                        <li key={idx} className="text-xs text-blue-800 flex items-start gap-1">
                          <span className="text-blue-600">•</span>
                          <span className="flex-1">{doc.name || doc.title || 'Untitled'}</span>
                          <span className="text-blue-600 text-[10px]">{doc.category || 'N/A'}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowMassDownloadModal(false);
                  setMassDownloadFilters({ dateFrom: '', dateTo: '', category: 'all', nameSearch: '' });
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={massDownloadFiltered}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                disabled={getFilteredDocumentsForDownload().length === 0}
              >
                Download {getFilteredDocumentsForDownload().length} Document(s)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Removal Request Modal */}
      {showRemovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">🗑️ Request Document Removal</h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                This document is in <span className="font-semibold text-blue-600">FINAL</span> status and cannot be deleted directly due to compliance requirements (7-year retention policy).
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Please provide a detailed reason for requesting removal. An administrator will review your request.
              </p>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Removal <span className="text-red-500">*</span>
              </label>
              <textarea
                value={removalReason}
                onChange={(e) => setRemovalReason(e.target.value)}
                placeholder="Please provide a detailed explanation (minimum 10 characters)..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                rows="4"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                {removalReason.length} / 10 characters minimum
              </p>
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowRemovalModal(false);
                  setRemovalDocId(null);
                  setRemovalReason('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={submitRemovalRequest}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!removalReason || removalReason.trim().length < 10}
              >
                Submit Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Replace Document Modal */}
      {showReplaceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">🔄 Replace Document</h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-semibold">Current Document:</span> {replaceDoc?.title}
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Select a new file to replace this document. The old version will be replaced with a new version.
              </p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New File <span className="text-red-500">*</span>
              </label>
              <input
                type="file"
                onChange={(e) => setReplaceFile(e.target.files[0])}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
              />
              {replaceFile && (
                <p className="mt-2 text-sm text-green-600">
                  ✓ Selected: {replaceFile.name}
                </p>
              )}
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Version Notes (Optional)
              </label>
              <textarea
                value={replaceNotes}
                onChange={(e) => setReplaceNotes(e.target.value)}
                placeholder="e.g., Updated budget figures for Q4, Fixed typos in section 3..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                rows="3"
              />
              <p className="mt-1 text-xs text-gray-500">
                Document history and previous versions are kept for audit purposes
              </p>
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowReplaceModal(false);
                  setReplaceDocId(null);
                  setReplaceDoc(null);
                  setReplaceFile(null);
                  setReplaceNotes('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={submitReplaceDocument}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!replaceFile}
              >
                Replace Document
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Upload Section - Moved to Bottom, Only show if user can upload */}
      {canUploadDocuments() && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">📤 Upload Documents</h3>
            
            {/* ⚠️ IMPORTANT NOTE */}
            <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-yellow-800">
                    ⚠️ Tag first, then upload - upload is instant!
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Select category, visibility, and folders BEFORE uploading files. Changes cannot be made during upload.
                  </p>
                </div>
              </div>
            </div>

            {/* Upload Configuration */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {documentCategories.map(category => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
              </div>

              {isBoardMember && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Visibility</label>
                  <select
                    value={selectedVisibility}
                    onChange={(e) => {
                      setSelectedVisibility(e.target.value);
                      // Clear folder selection if switching to "All Homeowners"
                      if (e.target.value === 'all') {
                        setSelectedFolders([]);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {permissionTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {selectedVisibility === 'specific' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Specific Users</label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search users by name, email, or group..."
                      value={userSearchTerm}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                      onFocus={() => setShowUserSearch(true)}
                      onBlur={() => setTimeout(() => setShowUserSearch(false), 300)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    {showUserSearch && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto">
                        {/* Enhanced Grouped User List */}
                        {(() => {
                          // Group users by type
                          const groupedUsers = filteredUsers.reduce((groups, user) => {
                            const group = user.type === 'board_member' ? 'Board Members' : 
                                         user.type === 'homeowner' ? 'Homeowners' :
                                         user.type === 'manual_folder' ? 'Manual Folders (Offline Users)' :
                                         user.type === 'maintenance' ? 'Maintenance' :
                                         user.type === 'security' ? 'Security' :
                                         user.type === 'staff' ? 'Staff' :
                                         user.type === 'attorney' ? 'Attorney' : 'Other';
                            if (!groups[group]) groups[group] = [];
                            groups[group].push(user);
                            return groups;
                          }, {});

                          // Sort groups: Board Members first, then Homeowners, Manual Folders, then external groups
                          const groupOrder = ['Board Members', 'Homeowners', 'Manual Folders (Offline Users)', 'Maintenance', 'Security', 'Staff', 'Attorney', 'Other'];
                          const sortedGroups = groupOrder.filter(group => groupedUsers[group]);

                          return sortedGroups.map(groupName => (
                            <div key={groupName}>
                              <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-700 border-b border-gray-200">
                                {groupName} ({groupedUsers[groupName].length})
                              </div>
                              {groupedUsers[groupName].map(user => (
                                <button
                                  key={user.id}
                                  onClick={() => {
                                    addSpecificUser(user.id);
                                    setUserSearchTerm('');
                                  }}
                                  disabled={specificUsers.includes(user.id)}
                                  className={`w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-b-0 ${
                                    specificUsers.includes(user.id) ? 'bg-green-50 opacity-60 cursor-not-allowed' : ''
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <div className="font-medium text-gray-900">{user.name}</div>
                                      <div className="text-sm text-gray-600">{user.email}</div>
                                      <div className="text-xs text-gray-500">{user.role}</div>
                                    </div>
                                    {specificUsers.includes(user.id) && (
                                      <span className="text-green-600 text-sm">✓ Selected</span>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          ));
                        })()}
                        
                        {filteredUsers.length === 0 && userSearchTerm && (
                          <div className="px-3 py-4 text-center text-gray-500">
                            No users found matching "{userSearchTerm}"
                          </div>
                        )}
                        
                        {availableUsers.length === 0 && userSearchTerm === '' && (
                          <div className="px-3 py-4 text-center text-gray-500">
                            Loading users...
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Selected Users with Groups */}
                  {specificUsers.length > 0 && (
                    <div className="mt-3">
                      <div className="text-sm font-medium text-gray-700 mb-2">
                        Selected Users ({specificUsers.length})
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {specificUsers.map(userId => {
                          const user = availableUsers.find(u => u.id === userId);
                          const groupColor = user?.type === 'board_member' ? 'bg-purple-100 text-purple-800' :
                                           user?.type === 'homeowner' ? 'bg-blue-100 text-blue-800' :
                                           user?.type === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                                           user?.type === 'security' ? 'bg-red-100 text-red-800' :
                                           user?.type === 'staff' ? 'bg-green-100 text-green-800' :
                                           user?.type === 'attorney' ? 'bg-gray-100 text-gray-800' :
                                           'bg-indigo-100 text-indigo-800';
                          
                          return (
                            <span
                              key={userId}
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${groupColor}`}
                            >
                              <span className="mr-1">
                                {user?.type === 'board_member' ? '👑' :
                                 user?.type === 'homeowner' ? '🏠' :
                                 user?.type === 'maintenance' ? '🔧' :
                                 user?.type === 'security' ? '🛡️' :
                                 user?.type === 'staff' ? '👤' :
                                 user?.type === 'attorney' ? '⚖️' : '👤'}
                              </span>
                              {getUserDisplayName(userId)}
                              <button
                                onClick={() => removeSpecificUser(userId)}
                                className="ml-1 text-current hover:text-gray-900"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* HOA Files Pro Folder Tagging - NEW FEATURE */}
            {isBoardMember && (
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📁 HOA Files Pro Folder (Optional)
                  {selectedVisibility === 'all' && (
                    <span className="ml-2 text-xs text-gray-500">(Disabled for "All Homeowners")</span>
                  )}
                </label>
                <select
                  value={selectedFolders.length > 0 ? selectedFolders[0] : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      setSelectedFolders([e.target.value]);
                    } else {
                      setSelectedFolders([]);
                    }
                  }}
                  disabled={selectedVisibility === 'all'}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 ${
                    selectedVisibility === 'all' ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''
                  }`}
                >
                  <option value="">No folder selected</option>
                  <optgroup label="Property Folders">
                    {availableFolders.filter(f => f.type === 'property').map(folder => (
                      <option key={folder.id} value={folder.id}>
                        {folder.label}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Manual Folders (Offline Users)">
                    {availableFolders.filter(f => f.type === 'manual').map(folder => (
                      <option key={folder.id} value={folder.id}>
                        {folder.label}
                      </option>
                    ))}
                  </optgroup>
                </select>
                <p className="mt-2 text-xs text-gray-500">
                  💡 Tag this document to a specific homeowner folder in HOA Files Pro. 
                  {selectedVisibility === 'all' ? ' Cannot tag to folders when "All Homeowners" is selected.' : 
                   ' This helps organize documents by property/owner.'}
                </p>
              </div>
            )}

            {/* Drag and Drop Zone - NOW AT THE BOTTOM */}
            <div
              ref={dropZoneRef}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors mt-6 ${
                isDragOver 
                  ? 'border-indigo-500 bg-indigo-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
                <svg fill="none" stroke="currentColor" viewBox="0 0 48 48">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" />
                </svg>
              </div>
              <p className="text-lg font-medium text-gray-900 mb-2">
                {isDragOver ? 'Drop files here' : 'Drag and drop files here'}
              </p>
              <p className="text-sm text-gray-600 mb-4">or</p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Choose Files
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="*"
                onChange={handleFileSelect}
                className="hidden"
              />
              <p className="text-xs text-gray-500 mt-2">
                Supports all file types. Multiple files can be selected for bulk upload.
              </p>
            </div>

            {/* Upload Progress */}
            {uploadingDocuments && (
              <div className="mt-4">
                <div className="text-sm text-gray-700 mb-2">Uploading {Object.keys(uploadProgress).length} document(s)...</div>
                {Object.entries(uploadProgress).map(([fileId, progress]) => (
                  <div key={fileId} className="mb-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
        </div>
      )}
    </div>
  );
};

// User Group Management Component
const UserGroupManagement = ({ availableUsers, onUserGroupUpdate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [managingUser, setManagingUser] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const externalGroups = [
    { id: '', name: 'None', icon: '👤', description: 'Regular homeowner' },
    { id: 'maintenance', name: 'Maintenance', icon: '🔧', description: 'Maintenance staff access' },
    { id: 'security', name: 'Security', icon: '🛡️', description: 'Security team access' },
    { id: 'staff', name: 'Staff', icon: '👥', description: 'General staff access' },
    { id: 'attorney', name: 'Attorney', icon: '⚖️', description: 'Legal counsel access' }
  ];

  const handleGroupChange = async (userId, newGroup) => {
    setIsUpdating(true);
    try {
      await api.post(`/api/users/${userId}/external-group`, {
        external_group: newGroup || null
      });
      
      alert(`User group ${newGroup ? 'updated to ' + newGroup : 'cleared'} successfully!`);
      onUserGroupUpdate(); // Refresh user list
      setManagingUser(null);
    } catch (error) {
      console.error('Failed to update user group:', error);
      alert('Failed to update user group. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  // Group users for display
  const groupedUsers = availableUsers.reduce((groups, user) => {
    const groupName = user.type === 'board_member' ? 'Board Members' : 
                     user.type === 'homeowner' ? 'Homeowners' : 
                     'External Members';
    if (!groups[groupName]) groups[groupName] = [];
    groups[groupName].push(user);
    return groups;
  }, {});

  if (!isOpen) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-blue-900">👥 User Group Management</h3>
            <p className="text-xs text-blue-700 mt-1">Assign external groups for document access control</p>
          </div>
          <button
            onClick={() => setIsOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm font-medium"
          >
            Manage Groups
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg leading-6 font-medium text-gray-900">👥 User Group Management</h3>
          <button
            onClick={() => setIsOpen(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-sm text-amber-800">
            <strong>Info:</strong> Assign external groups to users for better document access control. 
            Board members automatically have access to all documents.
          </p>
        </div>

        {Object.entries(groupedUsers).map(([groupName, users]) => (
          <div key={groupName} className="mb-6">
            <h4 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">
              {groupName} ({users.length})
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {users.map(user => (
                <div key={user.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      <p className="text-xs text-gray-600 mt-1">{user.role}</p>
                      {user.external_group && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                          {externalGroups.find(g => g.id === user.external_group)?.icon} {user.external_group}
                        </span>
                      )}
                    </div>
                    <div className="ml-2">
                      {user.type !== 'board_member' && (
                        <button
                          onClick={() => {
                            setManagingUser(user);
                            setSelectedGroup(user.external_group || '');
                          }}
                          className="text-indigo-600 hover:text-indigo-900 text-xs"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Group Assignment Modal */}
        {managingUser && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Assign Group: {managingUser.name}
              </h3>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-3">
                  Select an external group for this user:
                </p>
                
                <div className="space-y-2">
                  {externalGroups.map(group => (
                    <label key={group.id} className="flex items-center p-2 border rounded hover:bg-gray-50">
                      <input
                        type="radio"
                        name="external_group"
                        value={group.id}
                        checked={selectedGroup === group.id}
                        onChange={(e) => setSelectedGroup(e.target.value)}
                        className="mr-3"
                      />
                      <div className="flex items-center flex-1">
                        <span className="text-lg mr-2">{group.icon}</span>
                        <div>
                          <div className="font-medium text-gray-900">{group.name}</div>
                          <div className="text-xs text-gray-500">{group.description}</div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setManagingUser(null)}
                  disabled={isUpdating}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleGroupChange(managingUser.id, selectedGroup)}
                  disabled={isUpdating}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:opacity-50"
                >
                  {isUpdating ? 'Updating...' : 'Update Group'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Document Settings Sub-component
const DocumentSettings = ({ 
  documentSettings, 
  setDocumentSettings, 
  saveDocumentSettings,
  documentReviewDays,
  loadingReviewSetting,
  savingReviewSetting,
  saveDocumentReviewSetting,
  availableUsers
}) => {
  const [selectedUsers, setSelectedUsers] = useState(documentSettings.allowedUploadUsers || []);
  const [userSearchTerm, setUserSearchTerm] = useState('');

  // Sync selectedUsers when documentSettings changes
  React.useEffect(() => {
    setSelectedUsers(documentSettings.allowedUploadUsers || []);
  }, [documentSettings.allowedUploadUsers]);

  const toggleUserPermission = (userId) => {
    const newSelected = selectedUsers.includes(userId)
      ? selectedUsers.filter(id => id !== userId)
      : [...selectedUsers, userId];
    
    setSelectedUsers(newSelected);
    
    // Save to document settings immediately
    const newSettings = {
      ...documentSettings,
      allowedUploadUsers: newSelected
    };
    saveDocumentSettings(newSettings);
  };

  const filteredUsers = availableUsers.filter(u => 
    u.name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">⚙️ Document Settings</h2>
        <p className="text-sm text-gray-600 mt-1">
          Configure document review periods, permissions, and upload settings
        </p>
      </div>

      {/* Document Review Period Setting */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2">⏱️ Document Review Period</h3>
        <p className="text-sm text-gray-600 mb-4">
          Choose how long documents stay in "draft" status before automatically becoming final. 
          During the draft period, documents can be deleted or replaced. After becoming final, documents require admin approval for removal.
        </p>
        
        {loadingReviewSetting ? (
          <div className="text-sm text-gray-500">Loading setting...</div>
        ) : (
          <div className="space-y-3">
            <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
              style={{ borderColor: documentReviewDays === 0 ? '#3b82f6' : '#d1d5db' }}>
              <input
                type="radio"
                name="reviewDays"
                value="0"
                checked={documentReviewDays === 0}
                onChange={() => saveDocumentReviewSetting(0)}
                disabled={savingReviewSetting}
                className="mt-1 h-4 w-4 text-blue-600"
              />
              <div className="ml-3 flex-1">
                <div className="font-medium text-gray-900">
                  ⚡ Immediate (0 days) - Final immediately
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  Documents become final right away. Use this for established HOAs with strict document procedures.
                </div>
              </div>
            </label>

            <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
              style={{ borderColor: documentReviewDays === 3 ? '#3b82f6' : '#d1d5db' }}>
              <input
                type="radio"
                name="reviewDays"
                value="3"
                checked={documentReviewDays === 3}
                onChange={() => saveDocumentReviewSetting(3)}
                disabled={savingReviewSetting}
                className="mt-1 h-4 w-4 text-blue-600"
              />
              <div className="ml-3 flex-1">
                <div className="font-medium text-gray-900">
                  📝 Review Period (3 days) - Draft for 3 days
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  Documents remain editable for 3 days. Recommended for most HOAs - allows time to catch errors before documents become permanent records.
                </div>
              </div>
            </label>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 text-sm text-blue-800">
                  <p className="font-medium">Current Setting: {documentReviewDays === 0 ? 'Immediate (0 days)' : 'Review Period (3 days)'}</p>
                  <p className="mt-1">
                    {documentReviewDays === 0 
                      ? 'All new documents will become final records immediately upon upload.'
                      : 'New documents will be in draft status for 3 days, then automatically become final records.'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Document Permissions */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2">🔐 Document Upload Permissions</h3>
        <p className="text-sm text-gray-600 mb-4">
          Control who can upload documents to the library
        </p>

        <div className="space-y-4">
          {/* Global Permissions */}
          <div className="border-b pb-4">
            <h4 className="font-medium text-gray-900 mb-3">General Permissions</h4>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={documentSettings.allowHomeownerUpload}
                  onChange={(e) => {
                    const newSettings = { ...documentSettings, allowHomeownerUpload: e.target.checked };
                    setDocumentSettings(newSettings);
                    saveDocumentSettings(newSettings);
                  }}
                  className="h-4 w-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Allow all homeowners to upload documents
                </span>
              </label>
            </div>
          </div>

          {/* Specific User Permissions */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">
              Specific User Permissions
              <span className="text-sm font-normal text-gray-500 ml-2">
                (Grant upload permission to individual users)
              </span>
            </h4>
            
            <div className="mb-3">
              <input
                type="text"
                placeholder="Search users..."
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>

            <div className="border rounded-lg max-h-64 overflow-y-auto">
              {filteredUsers.length === 0 ? (
                <div className="p-4 text-sm text-gray-500 text-center">
                  No users found
                </div>
              ) : (
                filteredUsers.map(user => (
                  <label key={user.id} className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => toggleUserPermission(user.id)}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <div className="ml-3 flex-1">
                      <div className="text-sm font-medium text-gray-900">{user.name}</div>
                      <div className="text-xs text-gray-500">{user.email}</div>
                    </div>
                    {selectedUsers.includes(user.id) && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                        Can Upload
                      </span>
                    )}
                  </label>
                ))
              )}
            </div>

            <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-600">
                ℹ️ <strong>{selectedUsers.length} user(s)</strong> have specific document upload permission.
                {selectedUsers.length > 0 && ' These users can upload documents even if general homeowner upload is disabled.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedDocumentsTab;